import React, { useContext } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Tooltip, Menu, MenuItem, Box, Drawer } from '@mui/material'
import { SIDEBAR_WIDTH_COLLAPSED, drawerRootSx } from '@/layout-blueprints/chromeSx'
import useDesktopNav from '@/layout-blueprints/useDesktopNav'

import { connect } from 'react-redux'
import { setSidebarToggleMobile } from '../../reducers/ThemeOptions'

import { NavLink } from 'react-router'

import projectLogo from '@/assets/images/react.svg'

import VerifiedUserTwoToneIcon from '@mui/icons-material/VerifiedUserTwoTone'
import RoomTwoToneIcon from '@mui/icons-material/RoomTwoTone'
import CameraAltTwoToneIcon from '@mui/icons-material/CameraAltTwoTone'
import CollectionsTwoToneIcon from '@mui/icons-material/CollectionsTwoTone'
import FavoriteTwoToneIcon from '@mui/icons-material/FavoriteTwoTone'
import BusinessCenterTwoToneIcon from '@mui/icons-material/BusinessCenterTwoTone'
import AssessmentTwoToneIcon from '@mui/icons-material/AssessmentTwoTone'
import CameraTwoToneIcon from '@mui/icons-material/CameraTwoTone'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone'
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone'
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone'
import HelpTwoToneIcon from '@mui/icons-material/HelpTwoTone'
import FolderOpenTwoToneIcon from '@mui/icons-material/FolderOpenTwoTone'
import { PropertySearchContext } from '@/context/PropertySearchContext'

import {
  sidebarPaperSx,
  sidebarHeaderSx,
  sidebarLogoSx,
  sidebarLogoIconSx,
  sidebarContentSx,
  sidebarMenuCollapsedSx,
} from '@/layout-blueprints/chromeComponentSx'

const SidebarCollapsed = (props) => {
  const toggleSidebarMobile = () => {
    setSidebarToggleMobile(!sidebarToggleMobile)
  }

  const {
    sidebarShadow,
    sidebarStyle,
    sidebarToggleMobile,
    setSidebarToggleMobile,
  } = props

  const [propertySearchAnchorEl, setPropertySearchAnchorEl] =
    React.useState(null)
  const handlePropertySearchOpen = (event) =>
    setPropertySearchAnchorEl(event.currentTarget)
  const handlePropertySearchClose = () =>
    setPropertySearchAnchorEl(null)

  const { propertySearchOptions } = useContext(PropertySearchContext)
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)
  const urlLeadType = urlParams.get('leadType') || 'Leads'
  const isDesktop = useDesktopNav()

  return (
    <>
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : sidebarToggleMobile}
        onClose={toggleSidebarMobile}
        anchor="left"
        ModalProps={{ keepMounted: true }}
        sx={drawerRootSx({ collapsed: true, isDesktop })}
        slotProps={{
          paper: {
            'data-hf': 'chrome-sidebar-collapsed',
            sx: {
              ...sidebarPaperSx({
                collapsed: true,
                isMini: true,
                shadow: sidebarShadow,
                isDark: String(sidebarStyle || '').includes('dark'),
              }),
              width: SIDEBAR_WIDTH_COLLAPSED,
            },
          },
        }}
      >
        <Box sx={{ ...sidebarHeaderSx, justifyContent: 'center', px: 0, }} data-hf="sidebar-header">
          <Box sx={sidebarLogoSx} data-hf="sidebar-logo">
            <Box
              component={NavLink}
              to="/DashboardAnalytics"
              title="Bamburgh React Admin Dashboard with Material-UI PRO"
              sx={sidebarLogoSx}
            >
              <Box sx={sidebarLogoIconSx} data-hf="sidebar-logo-icon">
                <img
                  alt="Bamburgh React Admin Dashboard with Material-UI PRO"
                  src={projectLogo}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={sidebarContentSx} data-hf="sidebar-content">
          <Box sx={{
            position: "relative",
            height: "100%",
            maxHeight: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}>
            <Box component="ul" sx={{ ...sidebarMenuCollapsedSx, mt: '0.5rem' }} data-hf="sidebar-menu-collapsed">
              <li>
                <Tooltip title="Launchpad" arrow placement="right">
                  <NavLink
                    to="/launchpad"
                    end
                  >
                    <span>
                      <RocketLaunchIcon />
                    </span>
                  </NavLink>
                </Tooltip>
              </li>
              <li>
                <Tooltip
                  title="Property Search"
                  arrow
                  placement="right"
                >
                  <span
                    onClick={
                      propertySearchOptions &&
                      propertySearchOptions.length > 1
                        ? handlePropertySearchOpen
                        : undefined
                    }
                  >
                    <NavLink
                      to={
                        propertySearchOptions &&
                        propertySearchOptions.length === 1
                          ? '/property-search'
                          : '#'
                      }
                      end
                    >
                      <span>
                        <SearchTwoToneIcon />
                      </span>
                    </NavLink>
                  </span>
                </Tooltip>
                {propertySearchOptions &&
                  propertySearchOptions.length > 1 && (
                    <Menu
                      anchorEl={propertySearchAnchorEl}
                      open={Boolean(propertySearchAnchorEl)}
                      onClose={handlePropertySearchClose}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      {propertySearchOptions.map((option) => {
                        // Handle Creative MLS differently - open external URL
                        if (option === 'Creative MLS') {
                          return (
                            <MenuItem
                              key={option}
                              onClick={() => {
                                handlePropertySearchClose()
                                window.open(
                                  'https://www.thecreativelisting.com/',
                                  '_blank',
                                )
                              }}
                              sx={{ color: 'inherit' }}
                            >
                              {option}
                            </MenuItem>
                          )
                        }

                        // Handle regular property search options
                        return (
                          <MenuItem
                            key={option}
                            component={NavLink}
                            to={`/property-search?leadType=${encodeURIComponent(option)}`}
                            onClick={() => {
                              handlePropertySearchClose()
                              navigate(
                                `/property-search?leadType=${encodeURIComponent(option)}`,
                              )
                            }}
                          >
                            {option}
                          </MenuItem>
                        )
                      })}
                    </Menu>
                  )}
              </li>
              <li>
                <Tooltip
                  title="My Properties"
                  arrow
                  placement="right"
                >
                  <NavLink
                    to="/my-properties"
                    end
                  >
                    <span>
                      <HomeTwoToneIcon />
                    </span>
                  </NavLink>
                </Tooltip>
              </li>
              <li>
                <Tooltip title="Settings" arrow placement="right">
                  <NavLink
                    to="/settings/personal"
                    end
                  >
                    <span>
                      <SettingsTwoToneIcon />
                    </span>
                  </NavLink>
                </Tooltip>
              </li>
              <li>
                <Tooltip title="Support" arrow placement="right">
                  <NavLink
                    to="/support-chat"
                    end
                  >
                    <span>
                      <HelpTwoToneIcon />
                    </span>
                  </NavLink>
                </Tooltip>
              </li>
              <li>
                <Tooltip title="Portfolios" arrow placement="right">
                  <NavLink
                    to="/my-properties"
                    end
                  >
                    <span>
                      <FolderOpenTwoToneIcon />
                    </span>
                  </NavLink>
                </Tooltip>
              </li>
              <li>
                <Tooltip title="Trash" arrow placement="right">
                  <NavLink
                    to="/trash"
                    end
                  >
                    <span>
                      <FontAwesomeIcon icon={['fas', 'trash']} />
                    </span>
                  </NavLink>
                </Tooltip>
              </li>
              <li>
                <Tooltip title="Marketplaces" arrow placement="right">
                  <NavLink
                    to="/marketplace"
                    end
                  >
                    <span>
                      <FontAwesomeIcon
                        icon={['fas', 'sign-hanging']}
                      />
                    </span>
                  </NavLink>
                </Tooltip>
              </li>
            </Box>
            <Box sx={{ textAlign: 'center', mb: '0.5rem' }}>
              <Tooltip
                arrow
                placement="right"
                title="Back to dashboard"
              >
                <Button component={NavLink} variant="contained" to="/DashboardAnalytics" sx={[{
                  boxShadow: "none",
                  color: "#fff",
                  backgroundColor: "#f4772e",
                  backgroundImage: "none",
                  borderColor: "#f4772e",
                  "&:not(:disabled):hover": { backgroundColor: "#f4772e", color: "#fff" },
                  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#e35b0c" },
                  "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(244, 119, 46, 0.4), 0 0.0625rem 0.385rem rgba(244, 119, 46, 0.54)" },
                  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: 0,
                  height: "40px !important",
                  width: "40px !important",
                  lineHeight: "40px !important",
                  p: 0,
                  m: "0.25rem",
                  textAlign: "center",
                  borderRadius: "0.65rem",
                }, { fontSize: "1.15rem !important" }]}>
                  <FontAwesomeIcon icon={['fas', 'arrow-left']} />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

const mapStateToProps = (state) => ({
  sidebarShadow: state.DeprecatedThemeOptions.sidebarShadow,
  sidebarStyle: state.DeprecatedThemeOptions.sidebarStyle,
  sidebarToggleMobile:
    state.DeprecatedThemeOptions.sidebarToggleMobile,
})

const mapDispatchToProps = (dispatch) => ({
  setSidebarToggleMobile: (enable) =>
    dispatch(setSidebarToggleMobile(enable)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SidebarCollapsed)
